import { createContext, useContext, useReducer } from 'react';
import {
  createActionCreators,
  createReducerFunction,
  ImmerReducer,
} from 'immer-reducer';
import { QuoteDetails, SubmissionType } from '@converge/graphql';
import { toTitleCase } from '@converge/data';
import { Draft } from 'immer/dist/internal';

export enum QuoteLineItemType {
  Coverage = 'coverage',
  Endorsement = 'endorsement',
  Subjectivity = 'subjectivity',
  SubCoverage = 'subcoverage',
}

export const quoteLineItemTypeToLabel = (type: QuoteLineItemType) => {
  switch (type) {
    case QuoteLineItemType.Subjectivity:
      return 'Subjectivities';
    default:
      return `${toTitleCase(type)}s`;
  }
};

export interface IQuoteLineItem<
  LineItem extends QuoteLineItem = QuoteLineItem
> {
  policyType: SubmissionType.Primary;
  uuid: string;
  name: string;
  type: QuoteLineItemType;
  coverageType?: string | null;
  included: boolean;
  fusionScore?: number | null;
  modifiedCoveragePremium?: number | null;
  limit?: number | null;
  retention?: number | null;
  childItems?: LineItem[];
  isChildItem?: boolean;
  nonPremiumBearing?: boolean;
  parentCoverageUuid?: string;
}

export interface IExcessQuoteLineItem {
  policyType: SubmissionType.Excess;
  uuid: string;
  name: string;
  type?: QuoteLineItemType;
  layer: string;
  attachmentPoint?: number | null;
  boundPremium?: number | null;
  boundIncreasedLimitFactor?: number | null;
  limit?: number | null;
  policyNumber?: string | null;
  layerLimit?: number | null;
  layerAttachmentPoint?: number | null;
  layerPremium?: number | null;
  parentCoverageUuid?: string;
}

export type QuoteLineItem = IExcessQuoteLineItem | IQuoteLineItem;

export interface IQuoteLineItemGroup<
  LineItem extends QuoteLineItem = IQuoteLineItem
> {
  name: string;
  id: string;
  items: LineItem[];
  layerLimits: (number | null)[];
  layerAttachmentPoints: (number | null)[];
  layerPremiums: (number | null)[];
}

export interface IQuoteListContextState {
  quotes: QuoteDetails[];
  selectedQuotesUuids: string[];
  combinedQuoteLineItems: IQuoteLineItem[];
  showObsoleteQuotes: boolean;
  showQuoteOrderModel: boolean;
  showBindConfirmDialog: boolean;
  quoteToBindUuid?: string;
}

export class QuoteListReducer extends ImmerReducer<IQuoteListContextState> {
  constructor(
    draftState: Draft<IQuoteListContextState>,
    state: IQuoteListContextState
  ) {
    super(draftState, state);
    draftState.quotes = sortQuotes(draftState.quotes);
  }
  selectQuote(uuid: string) {
    this.draftState.selectedQuotesUuids.push(uuid);
    this.draftState.selectedQuotesUuids = [
      ...new Set(this.draftState.selectedQuotesUuids),
    ];
  }

  unselectQuote(uuid: string) {
    this.draftState.selectedQuotesUuids =
      this.draftState.selectedQuotesUuids.filter(
        (quoteUuid) => quoteUuid !== uuid
      );
  }

  toggleSelectedQuote(uuid: string) {
    if (this.draftState.selectedQuotesUuids.includes(uuid)) {
      this.unselectQuote(uuid);
    } else {
      this.selectQuote(uuid);
    }
  }

  toggleShowObsoleteQuotes(value = !this.draftState.showObsoleteQuotes) {
    this.draftState.showObsoleteQuotes = value;
  }

  setQuotes(quotes: QuoteDetails[]) {
    this.draftState.quotes = sortQuotes([...quotes]);
  }

  toggleSortQuoteModel(showQuoteOrderModel: boolean) {
    this.draftState.showQuoteOrderModel = showQuoteOrderModel;
  }

  toggleBindConfirmDialog(showBindConfirmDialog: boolean, quoteUuid?: string) {
    this.draftState.showBindConfirmDialog = showBindConfirmDialog;
    this.draftState.quoteToBindUuid = quoteUuid;
  }
}

export const QuoteListReducerFunction = createReducerFunction(QuoteListReducer);

export const QuoteListReducerActions = createActionCreators(QuoteListReducer);

export const QuoteListActionsContext = createContext<
  typeof QuoteListReducerActions
>(QuoteListReducerActions);

export const QuoteListDispatchContext =
  createContext<QuoteListReducerDispatch | null>(null);

export const QuoteListStateContext =
  createContext<IQuoteListContextState | null>(null);

export const createQuoteListState = (
  quotes: QuoteDetails[]
): IQuoteListContextState => ({
  selectedQuotesUuids: [],
  combinedQuoteLineItems: [],
  quotes: sortQuotes(quotes),
  showObsoleteQuotes: true,
  showQuoteOrderModel: false,
  showBindConfirmDialog: false,
});

export const useQuoteListReducer = (quotes: QuoteDetails[]) => {
  const [state, dispatch] = useReducer(
    QuoteListReducerFunction,
    createQuoteListState(quotes)
  );

  return [state, dispatch, QuoteListReducerActions] as [
    typeof state,
    typeof dispatch,
    typeof QuoteListReducerActions
  ];
};

export type QuoteListReducerState = ReturnType<typeof useQuoteListReducer>[0];

export type QuoteListReducerDispatch = ReturnType<
  typeof useQuoteListReducer
>[1];

export const useQuoteListDispatchContext = () => {
  const context = useContext(QuoteListDispatchContext);
  if (!context) {
    throw new Error(`Context not found for quote list`);
  }
  return context;
};

export const useQuoteListStateContext = () => {
  const context = useContext(QuoteListStateContext);
  if (!context) {
    throw new Error(`Context not found for quote list`);
  }
  return context;
};

export const useQuoteListActionsContext = () => {
  const context = useContext(QuoteListActionsContext);
  if (!context) {
    throw new Error(`Context not found for quote list`);
  }
  return context;
};

const sortQuotes = (quotes: QuoteDetails[]) => {
  return [...quotes].sort((a, b) => {
    if (a.isObsolete && !b.isObsolete) {
      return 1;
    }

    if (!a.isObsolete && b.isObsolete) {
      return -1;
    }

    return (a.order ?? 9999) - (b.order ?? 9999);
  });
};
